import { useEffect, useState } from 'react'
import { useStore } from '@w/react-store'
import { uid } from '@w/utils'
import type { QueryClient } from './query-client'
import { useQueryClient } from './query-client-provider'
import type { UseMutationOptions } from './use-mutation-types'

export type { UseMutationOptions } from './use-mutation-types'

export interface UseMutationResult<TData = unknown, TVariables = unknown> {
  data: TData | undefined
  error: Error | undefined
  isError: boolean
  isIdle: boolean
  isPending: boolean
  isSuccess: boolean
  failureCount: number
  mutate: (variables: TVariables) => void
  mutateAsync: (variables: TVariables) => Promise<TData>
  reset: () => void
  status: 'idle' | 'pending' | 'error' | 'success'
}

export function useMutation<TData = unknown, TVariables = unknown>(
  options: UseMutationOptions<TData, TVariables>,
  queryClient?: QueryClient,
): UseMutationResult<TData, TVariables> {
  const client = useQueryClient(queryClient)

  const [id] = useState(() => uid())

  // Sync mutation options and get mutation (runs on every render)
  const mutation = client.syncMutationOptions<TData, TVariables>(id, options)

  // Subscribe to mutation state via dedicated store
  const state = useStore(mutation.getStore())

  // Track mount/unmount for GC logic
  useEffect(() => {
    return mutation.subscribe(() => {
      // No-op callback - we only need this for mount/unmount tracking
    })
  }, [mutation])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      client.removeMutation(id)
    }
  }, [client, id])

  return {
    data: state.data,
    error: state.error,
    isError: state.status === 'error',
    isIdle: state.status === 'idle',
    isPending: state.status === 'pending',
    isSuccess: state.status === 'success',
    failureCount: state.failureCount,
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    reset: mutation.reset,
    status: state.status,
  }
}
